import React, { useState } from "react";
import { INITIAL_SUPPLIERS } from "../data/appData";

const CATEGORY_OPTIONS = [
  "Wood & Ply", "Paints", "Electrical", "Tiles & Flooring",
  "Glass & Mirrors", "Hardware & Fittings", "Furniture", "Fabric & Upholstery",
  "Lighting", "Plumbing", "False Ceiling", "Other"
];

const emptyForm = {
  supplierName: "", contactPerson: "", mobile: "", email: "",
  gst: "", category: "", city: "", address: "", creditDays: "", status: ""
};

function SupplierMaster() {
  const [suppliers, setSuppliers] = useState(INITIAL_SUPPLIERS);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewSupplier, setViewSupplier] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    const tempErrors = {};
    if (!formData.supplierName.trim()) tempErrors.supplierName = "Supplier name required";
    if (!formData.contactPerson.trim()) tempErrors.contactPerson = "Contact person required";
    if (!formData.mobile.trim()) tempErrors.mobile = "Mobile required";
    else if (!/^[0-9]{10}$/.test(formData.mobile)) tempErrors.mobile = "Enter valid 10-digit number";
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) tempErrors.email = "Enter valid email";
    if (formData.gst && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gst))
      tempErrors.gst = "Enter valid 15-digit GST number";
    if (!formData.category) tempErrors.category = "Select category";
    if (!formData.status) tempErrors.status = "Select status";
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (editId) {
      setSuppliers(suppliers.map(s => s.id === editId ? { ...formData, id: editId } : s));
    } else {
      setSuppliers([...suppliers, { ...formData, id: Date.now() }]);
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setErrors({});
    setEditId(null);
    setShowForm(false);
  };

  const handleEdit = (supplier) => {
    setFormData({ ...supplier });
    setEditId(supplier.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = (id) => {
    setSuppliers(suppliers.filter(s => s.id !== id));
    setDeleteConfirm(null);
  };

  const filteredSuppliers = suppliers.filter(s => {
    const matchSearch = search === "" ||
      s.supplierName.toLowerCase().includes(search.toLowerCase()) ||
      s.contactPerson.toLowerCase().includes(search.toLowerCase()) ||
      s.mobile.includes(search) ||
      (s.city && s.city.toLowerCase().includes(search.toLowerCase()));
    const matchCat = filterCategory === "" || s.category === filterCategory;
    const matchStatus = filterStatus === "" || s.status === filterStatus;
    return matchSearch && matchCat && matchStatus;
  });

  const activeCount = suppliers.filter(s => s.status === "Active").length;
  const categories = [...new Set(suppliers.map(s => s.category))];

  const categoryColors = {
    "Wood & Ply": "#8B4513", "Paints": "#4CAF50", "Electrical": "#FF9800",
    "Tiles & Flooring": "#9C27B0", "Glass & Mirrors": "#00BCD4",
    "Hardware & Fittings": "#607D8B", "Furniture": "#795548",
    "Fabric & Upholstery": "#E91E63", "Lighting": "#FFC107",
    "Plumbing": "#2196F3", "False Ceiling": "#FF5722", "Other": "#9E9E9E"
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h3 className="gold-text mb-0">Supplier Master</h3>
          <small className="text-muted">{suppliers.length} suppliers · {activeCount} active</small>
        </div>
        <button className="btn btn-warning fw-semibold" onClick={() => { setShowForm(!showForm); if (editId) resetForm(); }}>
          <i className={`bi ${showForm && !editId ? "bi-x-lg" : "bi-plus-lg"} me-2`}></i>
          {showForm && !editId ? "Cancel" : "Add Supplier"}
        </button>
      </div>

      {/* KPI Cards */}
      <div className="row g-3 mb-4">
        {[
          { label: "Total Suppliers", value: suppliers.length, icon: "bi-shop", color: "#b8860b" },
          { label: "Active", value: activeCount, icon: "bi-check-circle-fill", color: "#2e7d32" },
          { label: "Inactive", value: suppliers.length - activeCount, icon: "bi-pause-circle-fill", color: "#c62828" },
          { label: "Categories", value: categories.length, icon: "bi-grid-3x3-gap-fill", color: "#1565c0" },
        ].map((kpi, i) => (
          <div className="col-6 col-md-3" key={i}>
            <div className="card p-3">
              <div className="d-flex align-items-center gap-3">
                <div style={{ width: 44, height: 44, borderRadius: 10, background: kpi.color + "22", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className={`bi ${kpi.icon}`} style={{ fontSize: 20, color: kpi.color }}></i>
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>{kpi.label}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add / Edit Form */}
      {showForm && (
        <div className="card p-4 mb-4" style={{ border: "2px solid #b8860b22", background: editId ? "#fffbf0" : "#fff" }}>
          <h5 className="gold-text mb-3">
            <i className={`bi ${editId ? "bi-pencil-square" : "bi-shop"} me-2`}></i>
            {editId ? "Edit Supplier" : "Add New Supplier"}
          </h5>
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Supplier Name */}
              <div className="col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">Supplier / Company Name *</label>
                <input type="text" className={`form-control ${errors.supplierName ? "is-invalid" : ""}`}
                  name="supplierName" value={formData.supplierName} onChange={handleChange} placeholder="e.g. Greenply Industries" />
                {errors.supplierName && <div className="invalid-feedback">{errors.supplierName}</div>}
              </div>
              {/* Contact Person */}
              <div className="col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">Contact Person *</label>
                <input type="text" className={`form-control ${errors.contactPerson ? "is-invalid" : ""}`}
                  name="contactPerson" value={formData.contactPerson} onChange={handleChange} placeholder="Name of contact" />
                {errors.contactPerson && <div className="invalid-feedback">{errors.contactPerson}</div>}
              </div>
              {/* Mobile */}
              <div className="col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">Mobile *</label>
                <input type="text" className={`form-control ${errors.mobile ? "is-invalid" : ""}`}
                  name="mobile" value={formData.mobile} onChange={handleChange} maxLength={10} placeholder="10-digit number" />
                {errors.mobile && <div className="invalid-feedback">{errors.mobile}</div>}
              </div>
              {/* Email */}
              <div className="col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">Email</label>
                <input type="email" className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  name="email" value={formData.email} onChange={handleChange} placeholder="supplier@email.com" />
                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
              </div>
              {/* GST */}
              <div className="col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">GST Number</label>
                <input type="text" className={`form-control ${errors.gst ? "is-invalid" : ""}`}
                  name="gst" value={formData.gst} onChange={handleChange} maxLength={15} placeholder="27AABCG1234A1Z5" style={{ textTransform: "uppercase" }} />
                {errors.gst && <div className="invalid-feedback">{errors.gst}</div>}
              </div>
              {/* Category */}
              <div className="col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">Category *</label>
                <select className={`form-select ${errors.category ? "is-invalid" : ""}`}
                  name="category" value={formData.category} onChange={handleChange}>
                  <option value="">Select Category</option>
                  {CATEGORY_OPTIONS.map(c => <option key={c}>{c}</option>)}
                </select>
                {errors.category && <div className="invalid-feedback">{errors.category}</div>}
              </div>
              {/* City */}
              <div className="col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">City</label>
                <input type="text" className="form-control" name="city" value={formData.city} onChange={handleChange} placeholder="Mumbai, Delhi..." />
              </div>
              {/* Credit Days */}
              <div className="col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">Credit Days</label>
                <input type="number" className="form-control" name="creditDays" value={formData.creditDays} onChange={handleChange} placeholder="e.g. 30" min="0" />
              </div>
              {/* Status */}
              <div className="col-md-6 col-lg-4 mb-3">
                <label className="form-label fw-semibold">Status *</label>
                <select className={`form-select ${errors.status ? "is-invalid" : ""}`}
                  name="status" value={formData.status} onChange={handleChange}>
                  <option value="">Select Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                {errors.status && <div className="invalid-feedback">{errors.status}</div>}
              </div>
              {/* Address */}
              <div className="col-12 mb-3">
                <label className="form-label fw-semibold">Address</label>
                <textarea className="form-control" name="address" value={formData.address} onChange={handleChange} rows={2} placeholder="Full address..." />
              </div>
            </div>
            <div className="text-end d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancel</button>
              <button type="submit" className="btn btn-warning fw-semibold">
                <i className={`bi ${editId ? "bi-check2" : "bi-save"} me-2`}></i>
                {editId ? "Update Supplier" : "Save Supplier"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="card p-3 mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-md-5">
            <input className="form-control" placeholder="🔍 Search by name, contact, mobile, city..."
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="col-md-3">
            <select className="form-select" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
              <option value="">All Categories</option>
              {CATEGORY_OPTIONS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="col-md-2">
            <select className="form-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div className="col-md-2 text-end">
            <span className="text-muted small">{filteredSuppliers.length} results</span>
            {(search || filterCategory || filterStatus) && (
              <button className="btn btn-sm btn-link text-danger ms-2 p-0"
                onClick={() => { setSearch(""); setFilterCategory(""); setFilterStatus(""); }}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Supplier Table */}
      <div className="card" style={{ overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table className="table table-hover mb-0">
            <thead style={{ background: "#3e2723", color: "#f5f5dc" }}>
              <tr>
                <th className="px-3 py-3">#</th>
                <th>Supplier Name</th>
                <th>Contact Person</th>
                <th>Mobile</th>
                <th>Category</th>
                <th>City</th>
                <th>Credit Days</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSuppliers.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-5 text-muted">
                    <i className="bi bi-inbox" style={{ fontSize: 36, display: "block", marginBottom: 8 }}></i>
                    No suppliers found
                  </td>
                </tr>
              ) : filteredSuppliers.map((s, i) => (
                <tr key={s.id} style={{ verticalAlign: "middle" }}>
                  <td className="px-3 text-muted">{i + 1}</td>
                  <td>
                    <div className="fw-semibold">{s.supplierName}</div>
                    {s.gst && <small className="text-muted">{s.gst}</small>}
                  </td>
                  <td>
                    <div>{s.contactPerson}</div>
                    {s.email && <small className="text-muted">{s.email}</small>}
                  </td>
                  <td>{s.mobile}</td>
                  <td>
                    <span className="badge" style={{
                      background: (categoryColors[s.category] || "#888") + "22",
                      color: categoryColors[s.category] || "#888",
                      border: `1px solid ${(categoryColors[s.category] || "#888")}44`,
                      fontWeight: 600, fontSize: 11
                    }}>
                      {s.category}
                    </span>
                  </td>
                  <td>{s.city || "—"}</td>
                  <td>
                    {s.creditDays ? (
                      <span className="badge bg-light text-dark border">{s.creditDays} days</span>
                    ) : "—"}
                  </td>
                  <td>
                    <span className={`badge ${s.status === "Active" ? "bg-success" : "bg-secondary"}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="text-center">
                    <div className="d-flex gap-1 justify-content-center">
                      <button className="btn btn-sm btn-outline-info" title="View" onClick={() => setViewSupplier(s)}>
                        <i className="bi bi-eye"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-warning" title="Edit" onClick={() => handleEdit(s)}>
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => setDeleteConfirm(s.id)}>
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="modal-backdrop-custom" style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1050,
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <div className="card p-4" style={{ maxWidth: 380, width: "90%", textAlign: "center" }}>
            <i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: 40, marginBottom: 12 }}></i>
            <h5>Delete Supplier?</h5>
            <p className="text-muted mb-4">This action cannot be undone.</p>
            <div className="d-flex gap-2 justify-content-center">
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewSupplier && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1050,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16
        }}>
          <div className="card p-4" style={{ maxWidth: 520, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h5 className="gold-text mb-0">{viewSupplier.supplierName}</h5>
                <small className="text-muted">{viewSupplier.category}</small>
              </div>
              <button className="btn btn-sm btn-light" onClick={() => setViewSupplier(null)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="row g-3">
              {[
                { label: "Contact Person", value: viewSupplier.contactPerson, icon: "bi-person" },
                { label: "Mobile", value: viewSupplier.mobile, icon: "bi-phone" },
                { label: "Email", value: viewSupplier.email || "—", icon: "bi-envelope" },
                { label: "GST Number", value: viewSupplier.gst || "—", icon: "bi-file-earmark-text" },
                { label: "City", value: viewSupplier.city || "—", icon: "bi-geo-alt" },
                { label: "Credit Days", value: viewSupplier.creditDays ? `${viewSupplier.creditDays} days` : "—", icon: "bi-calendar-check" },
                { label: "Status", value: viewSupplier.status, icon: "bi-toggle-on" },
              ].map((item, i) => (
                <div className="col-6" key={i}>
                  <small className="text-muted d-block"><i className={`bi ${item.icon} me-1`}></i>{item.label}</small>
                  <strong>{item.value}</strong>
                </div>
              ))}
              {viewSupplier.address && (
                <div className="col-12">
                  <small className="text-muted d-block"><i className="bi bi-map me-1"></i>Address</small>
                  <strong>{viewSupplier.address}</strong>
                </div>
              )}
            </div>
            <div className="text-end mt-3">
              <button className="btn btn-warning btn-sm me-2" onClick={() => { setViewSupplier(null); handleEdit(viewSupplier); }}>
                <i className="bi bi-pencil me-1"></i>Edit
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setViewSupplier(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SupplierMaster;
